(() => {
  'use strict';
  const reduceMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  function initRail(rail) {
    if (rail.dataset.nlReady) return;
    rail.dataset.nlReady = 'true';
    const arrows = rail.parentElement.querySelector('[data-nl-arrows]');
    const prev = arrows?.querySelector('[data-nl-prev]');
    const next = arrows?.querySelector('[data-nl-next]');
    const move = (direction) => {
      const card = [...rail.children].find(item => !item.hidden);
      if (!card) return;
      const distance = card.getBoundingClientRect().width + parseFloat(getComputedStyle(rail).columnGap || 0);
      rail.scrollBy({ left: distance * direction, behavior: reduceMotion() ? 'auto' : 'smooth' });
    };
    const sync = () => {
      if (!arrows) return;
      arrows.hidden = rail.scrollWidth <= rail.clientWidth + 3;
      prev.disabled = rail.scrollLeft <= 3;
      next.disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 4;
    };
    prev?.addEventListener('click', () => move(-1));
    next?.addEventListener('click', () => move(1));
    rail.addEventListener('keydown', event => {
      if (event.target !== rail) return;
      if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') move(event.key === 'ArrowLeft' ? -1 : 1);
        else rail.scrollTo({left:event.key === 'Home' ? 0 : rail.scrollWidth,behavior:reduceMotion()?'auto':'smooth'});
      }
    });
    rail.addEventListener('scroll', sync, {passive:true});
    rail.addEventListener('nl:rail:update', sync);
    new ResizeObserver(sync).observe(rail);
    sync();
  }
  function initUGC(root) {
    if (root.dataset.nlReady) return;
    root.dataset.nlReady = 'true';
    const videos = Array.from(root.querySelectorAll('[data-nl-video]'));
    const label = video => video.closest('[data-nl-sound]').querySelector('[data-nl-sound-label]');
    const paintSound = video => {
      const button = video.closest('[data-nl-sound]');
      const on = !video.muted;
      const text = on ? 'Sound on' : 'Tap for sound';
      button.setAttribute('aria-pressed', String(on));
      // the accessible name starts with the words on screen (WCAG 2.5.3)
      button.setAttribute('aria-label', `${text}: ${video.getAttribute('aria-label')}`);
      label(video).textContent = text;
      const card = button.closest('.nl-video-card');
      if (card) card.classList.toggle('is-sound', on);
      root.classList.toggle('has-sound', videos.some(v => !v.muted));
    };
    const muteAll = except => videos.forEach(video => { if (video !== except) {video.muted = true; paintSound(video);} });
    // As on BAO (owner, 28 Sep 2026: "ugc video should be continuously played"): every visitor sees the clips play on
    // their own, muted and looping, including with Reduce Motion or Data Saver on. Clips on screen play together; clips
    // off screen pause and stop downloading, and carry on when they are back on screen. Where the browser still blocks
    // autoplay (for example an iPhone in Low Power Mode), the first tap or key press anywhere on the page starts them.
    const onScreen = new Set();
    const unlockEvents = ['touchend', 'click', 'keydown'];
    let unlockArmed = false;
    root.classList.add('nl-ugc--js');
    function unlock() {
      unlockEvents.forEach(type => document.removeEventListener(type, unlock, true));
      unlockArmed = false;
      onScreen.forEach(video => { if (video.paused) playMuted(video); });
    }
    function playMuted(video) {
      video.loop = true;
      video.playsInline = true;
      const attempt = video.play();
      if (attempt && attempt.catch) attempt.catch(() => {
        if (!video.paused) return;
        label(video).textContent = 'Tap to watch with sound';
        if (!unlockArmed) {
          unlockArmed = true;
          unlockEvents.forEach(type => document.addEventListener(type, unlock, true));
        }
      });
    }
    videos.forEach(video => {
      video.defaultMuted = true;
      video.muted = true;
      const button = video.closest('[data-nl-sound]');
      // the poster stays behind the clip, so the card never flashes empty while the clip fades in
      if (video.poster) button.style.backgroundImage = `url("${video.poster}")`;
      paintSound(video);
      video.addEventListener('volumechange', () => paintSound(video));
      video.addEventListener('playing', () => { video.classList.add('is-live'); paintSound(video); });
      button.addEventListener('click', () => {
        const turnOn = video.muted || video.paused;
        muteAll(video);
        video.muted = !turnOn;
        paintSound(video);
        // play() stays in the trusted click event for browsers that block autoplay; turning sound off keeps it playing.
        if (turnOn) video.play().catch(() => { video.muted = true; paintSound(video); });
      });
      video.addEventListener('error', () => {
        label(video).textContent = 'Video unavailable';
      });
    });
    if ('IntersectionObserver' in window) {
      const watcher = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          const video = entry.target;
          if (entry.isIntersecting) {
            onScreen.add(video);
            playMuted(video);
          } else {
            onScreen.delete(video);
            if (!video.muted) { video.muted = true; paintSound(video); }
            video.pause();
          }
        });
      }, {threshold: 0.3});
      videos.forEach(video => watcher.observe(video));
    } else videos.forEach(playMuted);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) muteAll();
      else onScreen.forEach(playMuted);
    });
  }

  function initExplorer(root) {
    if (root.dataset.nlReady) return;
    root.dataset.nlReady = 'true';
    const chapters = [...root.querySelectorAll('[data-nl-chapter]')];
    const links = [...root.querySelectorAll('[data-nl-ingredient-link]')];
    const sync = () => links.forEach(link => link.setAttribute('aria-expanded', String(chapters.find(d => '#'+d.id === link.hash)?.open || false)));
    const select = (chapter, scroll=false, focus=false) => {
      chapters.forEach(item => { item.open = item === chapter; });
      sync();
      const selected = chapter && links.find(link => link.hash === '#'+chapter.id);
      const rail = root.querySelector('.nl-ingredient-rail');
      if (rail && selected) {
        const left = selected.getBoundingClientRect().left - rail.getBoundingClientRect().left + rail.scrollLeft;
        rail.scrollTo({left:Math.max(0,left-(rail.clientWidth-selected.clientWidth)/2),behavior:'instant'});
      }
      if (scroll && chapter) {
        const top=chapter.getBoundingClientRect().top+window.scrollY-110;
        window.scrollTo({top,behavior:'instant'});
      }
      if (focus && chapter) {
        const title=chapter.querySelector('h3');title.tabIndex=-1;title.focus({preventScroll:true});
      }
    };
    const fromHash = (scroll=true) => {
      const target=chapters.find(item => '#'+item.id === location.hash);
      if (target) select(target,scroll);
      return target;
    };
    select(fromHash(false) || null);
    root.classList.add('is-enhanced');
    root.querySelectorAll('[data-nl-close-ingredient]').forEach(button => { button.hidden=false; });
    if (chapters.some(item => '#'+item.id === location.hash)) fromHash();
    links.forEach((link,index) => {
      link.addEventListener('click',event => {
        event.preventDefault();event.stopPropagation();const target=chapters.find(item => '#'+item.id === link.hash);
        if (!target) return;
        if (target.open) { closeChapter(link); }
        else {select(target,true,true);history.replaceState(null,'',link.hash);}
      });
      link.addEventListener('keydown',event => {
        if (!['ArrowLeft','ArrowRight','ArrowDown','ArrowUp','Home','End'].includes(event.key)) return;
        event.preventDefault();const next=event.key==='Home'?0:event.key==='End'?links.length-1:(index+(['ArrowRight','ArrowDown'].includes(event.key)?1:-1)+links.length)%links.length;
        links[next].focus();
      });
    });
    chapters.forEach(chapter => {
      chapter.querySelector('summary').addEventListener('click',event => {
        event.preventDefault();
        if (chapter.open) {chapter.open=false;sync();}
        else {select(chapter,true);history.replaceState(null,'','#'+chapter.id);}
      });
      chapter.addEventListener('toggle',sync);
    });
    function closeChapter(opener) {
      const selected = opener || links.find(item => item.getAttribute('aria-expanded') === 'true') || links[0];
      select(null);
      history.replaceState(null,'','#nrgt-ingredient-cards');
      const picker = root.querySelector('.nl-ingredient-picker');
      window.scrollTo({top:picker.getBoundingClientRect().top+window.scrollY-100,behavior:'instant'});
      selected?.focus({preventScroll:true});
    }
    root.querySelectorAll('[data-nl-back-ingredients],[data-nl-close-ingredient]').forEach(link => link.addEventListener('click',event => {
      event.preventDefault();event.stopPropagation();closeChapter();
    }));
    root.addEventListener('keydown',event => {
      if(event.key==='Escape' && event.target.closest('[data-nl-chapter]')) {event.preventDefault();closeChapter();}
    });
    window.addEventListener('hashchange',()=>fromHash());
  }
  function initProof(root) {
    if (root.dataset.nlReady) return;
    root.dataset.nlReady='true';
    const panels=[...root.querySelectorAll('[data-nl-proof-panel]')];
    const links=[...root.querySelectorAll('[data-nl-proof-link]')];
    let opener=null;
    const select=(panel,scroll=false)=>{
      panels.forEach(item=>{item.open=item===panel;});
      links.forEach(link=>link.setAttribute('aria-expanded',String(panel?.id===link.hash.slice(1))));
      if(panel&&scroll){
        window.scrollTo({top:panel.getBoundingClientRect().top+window.scrollY-110,behavior:'instant'});
        panel.querySelector('h4').focus({preventScroll:true});
      }
    };
    const fromHash=()=>{
      const panel=panels.find(item=>'#'+item.id===location.hash);
      if(panel){opener=links.find(link=>link.hash===location.hash);select(panel,true);}
    };
    const close=()=>{
      select(null);history.replaceState(null,'','#nrgt-label-cards');
      const picker=root.querySelector('.nl-proof-picker');
      window.scrollTo({top:picker.getBoundingClientRect().top+window.scrollY-100,behavior:'instant'});
      opener?.focus({preventScroll:true});
    };
    document.addEventListener('click', event => {
      const link = event.target.closest && event.target.closest('a[href^="#"]');
      if (!link || root.contains(link)) return;
      const panel = panels.find(item => '#' + item.id === link.getAttribute('href'));
      if (!panel) return;
      event.preventDefault(); event.stopPropagation();
      opener = link; select(panel, true); history.replaceState(null, '', link.getAttribute('href'));
    }, true);
    root.classList.add('is-enhanced');
    root.querySelectorAll('[data-nl-proof-close]').forEach(button=>{button.hidden=false;button.addEventListener('click',close);});
    links.forEach(link=>link.addEventListener('click',event=>{
      event.preventDefault();event.stopPropagation();opener=link;
      const panel=panels.find(item=>item.id===link.hash.slice(1));
      if(panel.open)close();else{select(panel,true);history.replaceState(null,'',link.hash);}
    }));
    root.addEventListener('keydown',event=>{if(event.key==='Escape'&&event.target.closest('[data-nl-proof-panel]')){event.preventDefault();close();}});
    fromHash();window.addEventListener('hashchange',fromHash);
  }
  function initPhotoViewer(dialog) {
    if (dialog.dataset.nlReady || typeof dialog.showModal !== 'function') return;
    dialog.dataset.nlReady = 'true';
    let opener = null;
    const section = dialog.previousElementSibling;
    section?.querySelectorAll('[data-nl-photo-open]').forEach(link => {
      link.addEventListener('click', event => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        opener = link;
        const photo = dialog.querySelector('[data-nl-photo-full]');
        dialog.classList.toggle('nl-photo-dialog--powder', link.dataset.nlPhotoFit === 'powder');
        photo.src = link.href;
        photo.alt = link.dataset.nlPhotoTitle;
        dialog.querySelector('#nl-photo-title').textContent = link.dataset.nlPhotoTitle;
        dialog.querySelector('[data-nl-photo-caption]').textContent = link.dataset.nlPhotoDescription;
        dialog.showModal();
      });
    });
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
    });
    dialog.addEventListener('close', () => {opener?.focus({preventScroll:true});});
  }
  function initMotion(scope) {
    if (reduceMotion() || !('IntersectionObserver' in window)) return;
    // Entrances run only once this class is on <html>, so the page stays complete and still without motion or scripts
    // (one motion system for Masala, Lemon and Peach, 28 Sep 2026).
    document.documentElement.classList.add('nl-motion-ready');
    // The science photo cards and the How-to-make wave get one entrance each (marked here, not in the markup).
    scope.querySelectorAll('.nl-ingredient-card, .nl-proof-card, .nl-mix-wave').forEach(item => {
      if (!item.hasAttribute('data-nl-reveal')) item.setAttribute('data-nl-reveal', '');
    });
    // Cards that enter together on wide screens arrive one after another (0, 90, 180, 270 ms); on phones each card is
    // revealed as it is swiped in, so there is no wait. The three steps always follow each other.
    const wide = matchMedia('(min-width: 750px)').matches;
    scope.querySelectorAll('.nl-occasion-rail, .nl-ingredient-rail, .nl-proof-rail, .nl-mix-steps').forEach(group => {
      const steps = group.matches('.nl-mix-steps');
      [...group.children].forEach((item, index) => item.style.setProperty('--nl-reveal-delay', (wide || steps ? (index % 4) * 90 : 0) + 'ms'));
    });
    // An entrance starts once any part of the element is on screen and above the bottom 8% of the window, so a card
    // that only peeks in at the edge of a rail shows its photo instead of waiting, empty, behind its curtain.
    const observer=new IntersectionObserver(entries=>entries.forEach(entry=>{
      if(!entry.isIntersecting)return;entry.target.classList.add('nl-reveal-in');observer.unobserve(entry.target);
    }),{threshold:0,rootMargin:'0px 0px -8% 0px'});
    scope.querySelectorAll('[data-nl-reveal]').forEach(item=>observer.observe(item));
  }
  function boot(scope=document) {
    scope.querySelectorAll('[data-nl-explorer]').forEach(initExplorer);
    scope.querySelectorAll('[data-nl-proof]').forEach(initProof);
    scope.querySelectorAll('[data-nl-photo-dialog]').forEach(initPhotoViewer);
    initMotion(scope);
    scope.querySelectorAll('[data-nl-rail]').forEach(initRail);
    scope.querySelectorAll('[data-nl-ugc]').forEach(initUGC);
  }
  boot();
  document.addEventListener('shopify:section:load', event => boot(event.target));
})();
